import React, { Component } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { noDataFound } from "../../../helpers/messages";
import {
	graphContextMenuItems,
	operativeProcedureTypes,
} from "../../../helpers/constants";
import { exportAddOns } from "../../../helpers/ChartExports";
import {
	getLearningCurveDisclaimer,
	getTimingDisclaimer,
} from "../../../helpers/GlobalFunctions";
import { Tabs, Tab, Nav } from "react-bootstrap";
import ErrorMsgBlockComponent from "../../Error/ErrorMessageBlock";
import OperativeApproachToggleComponent from "../TSADashboard/OperativeApproachToggle";

const AverageCaseTimeGraph = ({
	pieChartConfig,
	operativeProcedure,
	getTimingDisclaimer,
}) => {
	let graphHeight = "290px";
	if (operativeProcedure === operativeProcedureTypes.HIP.text) {
		graphHeight = "265px";
	} else if (operativeProcedure === operativeProcedureTypes.TSA.text) {
		graphHeight = "250px";
	}
	return (
		<>
			<HighchartsReact
				highcharts={Highcharts}
				options={pieChartConfig}
				containerProps={{
					style: {
						height: graphHeight,
					},
				}}
			/>
			{operativeProcedure === operativeProcedureTypes.HIP.text ? (
				<div className="d-flex align-items-center justify-content-between mt-2">
					{getTimingDisclaimer()}
				</div>
			) : (
				""
			)}
		</>
	);
};

const LearningCurveGraph = ({
	lineChartConfig,
	operativeProcedure,
	getTimingDisclaimer,
}) => {
	let graphHeight = "290px";
	if (operativeProcedure === operativeProcedureTypes.HIP.text) {
		graphHeight = "265px";
	} else if (operativeProcedure === operativeProcedureTypes.TSA.text) {
		graphHeight = "250px";
	}
	return (
		<>
			<HighchartsReact
				highcharts={Highcharts}
				options={lineChartConfig}
				containerProps={{
					style: {
						height: graphHeight,
					},
				}}
			/>
			{operativeProcedure === operativeProcedureTypes.HIP.text ? (
				<div className="d-flex align-items-center justify-content-between mt-2">
					{getTimingDisclaimer()}
				</div>
			) : (
				""
			)}
		</>
	);
};

class AverageCaseTimeDetails extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTab: "avg",
			operativeApproach: "all",
		};
	}

	/**
	 * @description Function to handle tab change for case time quadrant
	 * @param key The identifier of the selected tab
	 * @memberof AverageCaseTimeDetails
	 */
	handleTabs = (key) => {
		if (key) {
			this.setState({ activeTab: key });
		}
	};

	/**
	 * @description Function to handle operative approach button changes
	 * @param key The identifier of the selected operative approach
	 * @memberof AverageCaseTimeDetails
	 */
	handleOperativeApproachChange = (type) => {
		this.setState({ operativeApproach: type });
	};

	render() {
		const { operativeProcedure, setuptimePreference, averageCaseTime = {}, averageTsaCasetime = {}, totalTimeData = [], totalTimeDataTsa = {} } = this.props;
		const { operativeApproach } = this.state;

		// AVG CASE TIME PIE CHART START
		var timingDataBySurgeryOrder = {};
		var colors = [];
		switch (operativeProcedure) {
			case operativeProcedureTypes.TKA.text:
				if (
					averageCaseTime &&
					Object.keys(averageCaseTime).length &&
					setuptimePreference
				) {
					// as per the requirement, Misc should be rolled into Setup
					averageCaseTime.setup =
						averageCaseTime.setup +
						(averageCaseTime.misc ? averageCaseTime.misc : "");
					delete averageCaseTime.misc;
				}
				timingDataBySurgeryOrder = {
					...(setuptimePreference && { Setup: averageCaseTime.setup }),
					Registration: averageCaseTime.registration,
					Planning: averageCaseTime.planning,
					Cutting: averageCaseTime.cutting,
					PostOp: averageCaseTime.postOp,
				};
				colors = ["#79c3ec", "#45b864", "#e2333b", "#a98bc0", "#f99d20"];
				break;

			case operativeProcedureTypes.HIP.text:
				timingDataBySurgeryOrder = {
					Registration: averageCaseTime.registration,
					Navigation: averageCaseTime.navigation,
				};
				colors = ["#45b864", "#e2333b"];
				break;

			case operativeProcedureTypes.TSA.text:
				let tsadata = {};
				if (operativeApproach === "all") {
					tsadata =
						averageTsaCasetime.all.length > 0 ? averageTsaCasetime.all[0] : {};
				} else if (operativeApproach === "anatomic") {
					tsadata =
						averageTsaCasetime.anatomic.length > 0
							? averageTsaCasetime.anatomic[0]
							: {};
				} else if (operativeApproach === "reverse") {
					tsadata =
						averageTsaCasetime.reverse.length > 0
							? averageTsaCasetime.reverse[0]
							: {};
				}

				if (tsadata && Object.keys(tsadata).length && setuptimePreference) {
					// as per the requirement, Misc should be rolled into Setup
					tsadata.setup = tsadata.setup + (tsadata.misc ? tsadata.misc : "");
					delete tsadata.misc;
				}

				timingDataBySurgeryOrder = {
					...(setuptimePreference && { Setup: tsadata.setup }),
					"Humeral Registration": tsadata.humeralregistration,
					"Humeral Cutting": tsadata.humeralcutting,
					"Glenoid Registration": tsadata.glenoidregistration,
					"Glenoid Cutting": tsadata.glenoidcutting,
					Planning: tsadata.planning,
					PostOp: tsadata.postopassessment,
				};
				colors = [
					"#79c3ec",
					"#45b864",
					"#a98bc0",
					"#21918e",
					"#812bc3",
					"#e2333b",
					"#f99d20",
				];
				break;
			default:
				break;
		}

		if ([operativeProcedureTypes.TKA.text, operativeProcedureTypes.TSA.text].indexOf(operativeProcedure) > -1 && !setuptimePreference) {
			colors.splice(0, 1);
		}

		let timingData = [];
		var totalMin = 0;
		for (const [key, value] of Object.entries(timingDataBySurgeryOrder)) {
			var getMin = parseFloat(value / 60).toFixed(1);
			var dataArray = [key, parseFloat(getMin)]; // Convert from second to minute
			totalMin += !isNaN(parseFloat(getMin)) ? parseFloat(getMin) : 0;
			timingData.push(dataArray);
		}

		/* istanbul ignore next  */
		var pieChartConfig = {
			exporting: {
				sourceWidth: 900,
				sourceHeight: 350,
				buttons: {
					contextButton: {
						menuItems: graphContextMenuItems,
					},
				},
				chartOptions: {
					title: {
						y: 30,
						x: 0,
						style: {
							fontSize: 10,
						},
					},
					plotOptions: {
						pie: {
							size: 210,
							innerSize: 100,
							dataLabels: {
								distance: 20,
								style: {
									fontSize: 10,
								},
							},
						},
					},
					chart: {
						marginTop: 40,
						marginBottom: 50,
						spacingBottom: 60,
						events: {
							load: function () {
								exportAddOns(this);
							},
						},
					},
					legend: {
						margin: 0,
						floating: true,
					},
					subtitle: {
						text: "Average Case Time",
						style: {
							fontFamily:
								"Lucida Grande, Lucida Sans Unicode, Arial, Helvetica, sans-serif",
							color: "#333333",
							fontSize: 18,
							textAlign: "center",
						},
					},
				},
			},
			chart: {
				type: "pie",
				events: {
					redraw: function () {
						if (operativeProcedure !== operativeProcedureTypes.TSA.text) {
							if (this.fullscreen && this.fullscreen.isOpen) {
								this.update(
									{
										title: { y: 20 },
										subtitle: {
											text: "Average Case Time",
											style: {
												fontFamily:
													"Lucida Grande, Lucida Sans Unicode, Arial, Helvetica, sans-serif",
												color: "#333333",
												fontSize: 18,
												textAlign: "center",
											},
										},
									},
									false
								);
							} else {
								this.update({ subtitle: { text: "" }, title: { y: 10 } }, false);
							}
						}
					},
				},
			},
			plotOptions: {
				pie: {
					center: ["50%", "50%"],
					colors: colors,
					innerSize: 100,
					showInLegend: true,
				},
			},
			tooltip: {
				enabled: true,
				formatter: function () {
					return (
						"<b>" +
						this.point.name +
						"</b>: " +
						parseFloat(this.point.y) +
						" MINUTES "
					);
				},
			},
			legend: {
				align: "right",
				verticalAlign: "middle",
				layout: "vertical",
				backgroundColor: Highcharts.defaultOptions.chart.backgroundColor,
				floating: false, // Default is false, but setting it explicitly
				borderWidth: 1,
			},
			title: {
				text: parseFloat(totalMin.toFixed(1)) + "<br/> Minutes",
				align: "center",
				verticalAlign: "middle",
				y: 10,
				x: operativeProcedure === operativeProcedureTypes.TSA.text ? -87 : -60,
				style: {
					fontWeight: "bold",
					fontSize: "12px",
					color: "#7c7c7b",
				},
			},
			series: [
				{
					name: "Timing Data",
					data: timingData,
					dataLabels: {
						enabled: true,
						formatter: function () {
							return parseFloat(this.point.y);
						},
					},
				},
			],
			responsive: {
				rules: [
					{
						condition: {
							maxWidth: 500,
						},
						chartOptions: {
							plotOptions: {
								pie: {
									innerSize: 60,
								},
							},
						},
					},
				],
			},
		};

		// AVG CASE TIME PIE CHART END

		// LEARNING CURVE GRAPH START
		let DataMyCase = [];
		let DataGlobalCase = [];
		let xAxis = [];

		const updatedTimingData =
			operativeProcedure === operativeProcedureTypes.TSA.text
				? totalTimeDataTsa[operativeApproach]
				: totalTimeData;
		updatedTimingData.forEach((data, i) => {
			xAxis.push(i + 1);
			DataMyCase.push({
				x: i,
				y: data.myCase ? parseFloat((data.myCase / 60).toFixed(1)) : null,
			});
			DataGlobalCase.push({
				x: i,
				y: data.globalAverage
					? parseFloat((data.globalAverage / 60).toFixed(1))
					: null,
			});
		});
		/* istanbul ignore next  */
		var lineChartConfig = {
			exporting: {
				sourceWidth: 900,
				sourceHeight: 350,
				filename: "Learning Curve",
				buttons: {
					contextButton: {
						menuItems: graphContextMenuItems,
					},
				},
				chartOptions: {
					title: {
						y: 30,
						x: 0,
						style: {
							fontSize: 10,
						},
					},
					plotOptions: {
						pie: {
							size: 210,
							innerSize: 100,
							dataLabels: {
								distance: 20,
								style: {
									fontSize: 10,
								},
							},
						},
					},
					chart: {
						marginTop: 40,
						marginBottom: 150,
						spacingBottom: 60,
						events: {
							load: function () {
								exportAddOns(this);
							},
						},
					},
					legend: {
						margin: 0,
						floating: true,
					},
					subtitle: {
						text: "Learning Curve",
						style: {
							fontFamily:
								"Lucida Grande, Lucida Sans Unicode, Arial, Helvetica, sans-serif",
							color: "#333333",
							fontSize: 18,
							textAlign: "center",
						},
					},
				},
			},
			chart: {
				type: "spline",
				events: {
					redraw: function () {
						if (this.fullscreen && this.fullscreen.isOpen) {
							this.update(
								{
									title: { y: 20 },
									subtitle: {
										text: "Average Case Time",
										style: {
											fontFamily:
												"Lucida Grande, Lucida Sans Unicode, Arial, Helvetica, sans-serif",
											color: "#333333",
											fontSize: 18,
											textAlign: "center",
										},
									},
								},
								false
							);
						} else {
							this.update({ subtitle: { text: "" }, title: { y: 10 } }, false);
						}
					},
				},
			},
			xAxis: {
				categories: xAxis,
				title: {
					text: "CASES",
				},
			},
			yAxis: {
				title: {
					text: "MINUTES",
				},
			},
			tooltip: {
				enabled: true,
				formatter: function () {
					function getTooltip(yValue, serie) {
						const val = Math.abs(yValue); // Taking absolute value from the plotted point
						return `${serie.name}: ${val} Minutes </strong>`;
					}
					var tooltip = getTooltip(this.y, this.series);
					const series = this.series.chart.series;
					for (let index = 0; index < series.length; index++) {
						const serie = series[index];
						const yValue = serie.processedYData[this.point.index];
						if (
							yValue === this.y &&
							this.series.name !== serie.name &&
							serie.visible
						) {
							tooltip += `<br/>${getTooltip(yValue, serie)}`;
						}
					}

					return tooltip;
				},
			},
			title: {
				text: "",
			},
			series: [
				{
					name: "Global Cases",
					data: DataGlobalCase,
					color: "#7ABA43"
				},
				{
					name: "My Cases",
					data: DataMyCase,
					color: "#DB6D4F"
				},
			],
		};
		// LEARNING CURVE GRAPH END

		// It should render based on the operative procedure if it is TSA then tabs position at top otherwise bottom
		const renderTabs = () => {
			if (operativeProcedure === operativeProcedureTypes.TSA.text) {
				return (
					<>
						<div className="dashboard-block-title d-flex align-items-center justify-content-between">
							{this.state.activeTab === "avg" ? (
								<span>Average Case Time</span>
							) : (
								<>
									<span>Learning Curve</span>
									<div className="mt-2">{getLearningCurveDisclaimer()}</div>
								</>
							)}
						</div>

						<Tabs
							id="case-time-tabs"
							defaultActiveKey="avg"
							onSelect={this.handleTabs}
							className="avg-case-time-tabs"
						>
							<Tab eventKey="avg" title="Average">
								<div className="tsa-avg-casetime-graph mt-2">
									{Object.keys(averageTsaCasetime[operativeApproach]).length !== 0 ? (
										<AverageCaseTimeGraph
											pieChartConfig={pieChartConfig}
											operativeProcedure={operativeProcedure}
											getTimingDisclaimer={getTimingDisclaimer}
										/>
									) : (
										<div className="no-data-found"> {noDataFound.noData} </div>
									)}
									<br />
								</div>
							</Tab>

							<Tab eventKey="compare" title="Learning Curve">
								<div className="tsa-avg-casetime-graph mt-2">
									{Object.keys(totalTimeDataTsa[operativeApproach]).length !==
										0 ? (
										<LearningCurveGraph
											lineChartConfig={lineChartConfig}
											operativeProcedure={operativeProcedure}
											getTimingDisclaimer={getTimingDisclaimer}
										/>
									) : (
										<ErrorMsgBlockComponent
											noData={true}
											className="no-data-found"
										/>
									)}
									<br />
								</div>
							</Tab>
						</Tabs>
					</>
				);
			} else {
				return (
					<Tab.Container
						defaultActiveKey={this.state.activeTab}
						onSelect={this.handleTabs}
					>
						<Tab.Content>
							<Tab.Pane eventKey="avg">
								<div className="dashboard-block-title d-flex align-items-center justify-content-between">
									<span>Average Case Time</span>
								</div>
								<div className="avg-casetime-graph mt-2">
									{Object.keys(averageCaseTime).length !== 0 ? (
										<AverageCaseTimeGraph
											pieChartConfig={pieChartConfig}
											operativeProcedure={operativeProcedure}
											getTimingDisclaimer={getTimingDisclaimer}
										/>
									) : (
										<div className="no-data-found"> {noDataFound.noData} </div>
									)}
									<br />
								</div>
							</Tab.Pane>
							<Tab.Pane eventKey="compare">
								<div className="dashboard-block-title d-flex align-items-center justify-content-between">
									<span>Learning Curve</span>
									<div className="mt-2">{getLearningCurveDisclaimer()}</div>
								</div>
								<div className="avg-casetime-graph mt-2">
									{Object.keys(totalTimeData).length !== 0 ? (
										<LearningCurveGraph
											lineChartConfig={lineChartConfig}
											operativeProcedure={operativeProcedure}
											getTimingDisclaimer={getTimingDisclaimer}
										/>
									) : (
										<ErrorMsgBlockComponent
											noData={true}
											className="no-data-found"
										/>
									)}
									<br />
								</div>
							</Tab.Pane>
						</Tab.Content>
						<Nav variant="pills" className="justify-content-center">
							<Nav.Item>
								<Nav.Link eventKey="avg" data-testid="avg-casetime">
									Average
								</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="compare" data-testid="learning-curve">
									Learning Curve
								</Nav.Link>
							</Nav.Item>
						</Nav>
					</Tab.Container>
				);
			}
		};

		return (
			<div className="dashboard-blocks">
				<div
					className="dashboard-blocks-body"
					data-testid="average-case-time-component"
				>
					<div className="quadrant-tabs">
						{renderTabs()}
						{operativeProcedure === operativeProcedureTypes.TSA.text && (
							<OperativeApproachToggleComponent
								operativeApproach={operativeApproach}
								onChange={(type) => this.handleOperativeApproachChange(type)}
							/>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export default AverageCaseTimeDetails;
